import { useMemo, useState } from "react";
import { useDelete, useNotify } from "ra-core";
import {
  Bell,
  Flag,
  Loader2,
  MoreVertical,
  Pencil,
  Trash2,
} from "lucide-react";
import { ReferenceField } from "@/components/admin/reference-field";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { OrganizationMemberName } from "@/components/atomic-crm/organizationMembers/OrganizationMemberName";
import { TaskAssignedAvatars } from "@/components/atomic-crm/tasks/TaskAssignedAvatars";
import {
  TaskCardActionCell,
  TaskCardRow,
  TaskCardTable,
  TaskRoundCheckbox,
  TaskSortHeader,
  type TaskCardColumn,
} from "@/components/atomic-crm/tasks/TaskCardTable";
import type {
  Deal,
  OrganizationMember,
  Task as TaskRecord,
  TaskParticipant,
} from "@/components/atomic-crm/types";
import { TaskEdit } from "@/components/atomic-crm/tasks/TaskEdit";
import { extractTaskDueTime } from "@/components/atomic-crm/tasks/taskDueDateTime";
import { isTaskOverdue } from "@/components/atomic-crm/tasks/taskStats";
import { TaskDescriptionCell } from "@/components/atomic-crm/tasks/TaskDescriptionCell";
import { useTaskCompletionToggle } from "@/components/atomic-crm/tasks/useTaskCompletionToggle";
import { useTaskParticipantsByTaskIds } from "@/components/atomic-crm/tasks/useTaskParticipants";
import { formatEventTimeLabel } from "@/modules/calendar/calendarReminderOptions";
import { parseDateKey } from "@/modules/calendar/calendarUtils";
import { cn } from "@/lib/utils";

const formatDueDate = (task: TaskRecord) => {
  if (!task.due_date) return "—";
  const due = new Date(task.due_date);
  if (Number.isNaN(due.getTime())) return "—";
  if (task.start_date) {
    const start = parseDateKey(task.start_date.slice(0, 10));
    const startLabel = start.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const dueLabel = due.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${startLabel} → ${dueLabel}`;
  }
  return due.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatReminderOffset = (minutes: number) => {
  if (minutes <= 0) return "At time";
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
  return `${Math.round(minutes / 1440)}d`;
};

const formatReminders = (offsets?: number[] | null) => {
  if (!offsets?.length) return null;
  const sorted = [...offsets].sort((a, b) => a - b);
  const labels = sorted.map(formatReminderOffset);
  return sorted.every((minutes) => minutes <= 0)
    ? labels[0]
    : `${labels.join(", ")} before`;
};

const dueTime = (task: TaskRecord) =>
  task.due_date ? new Date(task.due_date).getTime() : 0;

export const TaskTable = ({
  tasks,
  showProject = false,
  compact = false,
  emptyMessage = "No tasks yet.",
}: {
  tasks: TaskRecord[];
  showProject?: boolean;
  /** Narrow panels: hides Reminder and Project. */
  compact?: boolean;
  emptyMessage?: string;
}) => {
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const sortedTasks = useMemo(
    () =>
      [...tasks].sort((left, right) =>
        sortDirection === "asc"
          ? dueTime(left) - dueTime(right)
          : dueTime(right) - dueTime(left),
      ),
    [sortDirection, tasks],
  );
  const taskIds = useMemo(
    () => sortedTasks.map((task) => task.id),
    [sortedTasks],
  );
  const { participantsByTaskId } = useTaskParticipantsByTaskIds(taskIds);

  if (sortedTasks.length === 0) {
    return (
      <p className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
        {emptyMessage}
      </p>
    );
  }

  const columns: TaskCardColumn[] = [
    { key: "done", className: "w-10" },
    { key: "title", label: "Title" },
    {
      key: "due",
      label: (
        <TaskSortHeader
          label="Due date"
          direction={sortDirection}
          onToggle={() =>
            setSortDirection((current) => (current === "asc" ? "desc" : "asc"))
          }
        />
      ),
      className: "w-40",
    },
    { key: "time", label: "Due time", className: "w-24" },
    ...(compact
      ? []
      : [{ key: "reminder", label: "Reminder", className: "w-32" }]),
    ...(showProject && !compact
      ? [{ key: "project", label: "Project", className: "w-44" }]
      : []),
    { key: "assigned", label: "Assigned", className: "w-28" },
    { key: "actions", className: "w-12" },
  ];

  return (
    <TaskCardTable columns={columns}>
      {sortedTasks.map((task) => (
        <TaskTableRow
          key={String(task.id)}
          task={task}
          showProject={showProject && !compact}
          showReminder={!compact}
          participants={participantsByTaskId[String(task.id)] ?? []}
        />
      ))}
    </TaskCardTable>
  );
};

const TaskTableRow = ({
  task,
  showProject,
  showReminder,
  participants,
}: {
  task: TaskRecord;
  showProject: boolean;
  showReminder: boolean;
  participants: TaskParticipant[];
}) => {
  const notify = useNotify();
  const [deleteOne, { isPending: isDeleting }] = useDelete();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const {
    toggle,
    checkboxChecked,
    checkboxDisabled,
    usesParticipantCompletion,
  } = useTaskCompletionToggle(task, participants);

  const isFullyDone = Boolean(task.done_date);
  const isDoneForUser = usesParticipantCompletion
    ? checkboxChecked
    : isFullyDone;
  const overdue = isTaskOverdue(task) && !isDoneForUser;
  const timeLabel = formatEventTimeLabel(extractTaskDueTime(task.due_date));
  const reminderLabel = formatReminders(task.reminder_offsets_minutes);

  const handleDelete = async () => {
    try {
      await deleteOne(
        "tasks",
        { id: task.id, previousData: task },
        { returnPromise: true },
      );
      notify("Task deleted");
      setDeleteOpen(false);
    } catch {
      notify("Failed to delete task", { type: "error" });
    }
  };

  const assignedCell =
    (task.assignee_person_ids?.length ?? 0) > 0 ||
    (task.collaborator_person_ids?.length ?? 0) > 0 ||
    (task.mentioned_member_ids?.length ?? 0) > 0 ? (
      <TaskAssignedAvatars task={task} participants={participants} />
    ) : (
      <ReferenceField
        source="organization_member_id"
        reference="organization_members"
        record={task}
        render={({ referenceRecord }) => (
          <OrganizationMemberName
            member={referenceRecord as OrganizationMember | undefined}
          />
        )}
      />
    );

  return (
    <>
      <TaskCardRow onClick={() => setEditOpen(true)} muted={isDoneForUser}>
        <TaskCardActionCell>
          <TaskRoundCheckbox
            checked={checkboxChecked}
            disabled={checkboxDisabled}
            onCheckedChange={toggle}
            aria-label={
              usesParticipantCompletion
                ? checkboxChecked
                  ? "Mark my part open"
                  : "Mark my part done"
                : isFullyDone
                  ? "Mark task open"
                  : "Mark task done"
            }
          />
        </TaskCardActionCell>

        <td className="max-w-[28rem]">
          <div className="flex min-w-0 items-center gap-2">
            {task.priority === "high" ? (
              <Flag
                className="size-3.5 shrink-0 fill-destructive text-destructive"
                aria-label="High priority"
              />
            ) : null}
            <div className="min-w-0 flex-1">
              <TaskDescriptionCell
                text={task.text}
                isDone={isDoneForUser}
                useMentions
                enablePreview={false}
              />
            </div>
          </div>
        </td>

        <td
          className={cn(
            "whitespace-nowrap",
            overdue && "font-medium text-destructive",
          )}
        >
          {formatDueDate(task)}
        </td>

        <td className="whitespace-nowrap text-muted-foreground">
          {timeLabel?.toLowerCase() ?? "—"}
        </td>

        {showReminder ? (
          <td className="whitespace-nowrap text-muted-foreground">
            {reminderLabel ? (
              <span className="inline-flex items-center gap-1.5">
                <Bell className="size-3.5" />
                {reminderLabel}
              </span>
            ) : (
              "—"
            )}
          </td>
        ) : null}

        {showProject ? (
          <TaskCardActionCell className="max-w-[11rem]">
            {task.deal_id ? (
              <ReferenceField<TaskRecord, Deal>
                source="deal_id"
                reference="deals"
                record={task}
                link="show"
                render={({ referenceRecord }) =>
                  referenceRecord ? (
                    <span className="block truncate link-action">
                      {referenceRecord.name}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )
                }
              />
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </TaskCardActionCell>
        ) : null}

        <TaskCardActionCell>{assignedCell}</TaskCardActionCell>

        <TaskCardActionCell className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton aria-label="More actions">
                <MoreVertical className="size-4" />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditOpen(true)}>
                <Pencil className="size-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                disabled={isDeleting}
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="size-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TaskCardActionCell>
      </TaskCardRow>

      <TaskEdit open={editOpen} onOpenChange={setEditOpen} taskId={task.id} />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete task?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={isDeleting}
              onClick={handleDelete}
            >
              {isDeleting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
